import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import * as PR from "../../prime-modules/index";
import AdminFooter from "../layout/admin-footer";
import AdminHeader from "../layout/admin-header";
import { useNavigate, useParams } from 'react-router-dom';
import "../users/Users.scss"; // CSS
import { getOrderDetails, retryOrderStatus } from "../../services/api";
import { useDispatch, useSelector } from "react-redux";
import * as utils from '../../utils';
import Notes from "../notes/Notes";
import { formatPromoCode, orderStatuses, deliveryStatuses, formatDateTime, formatDate } from "../../utils/reuse";
import { emailRed } from "../../assets/images/index";
import SendEmail from "../../utils/sendEmail";
import OrderActionMenu from "../orders-list/OrderActionMenu";
import PriceDisplay from "../../components/PriceDisplay";


const OrderDetails = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);
    const toast = useRef(null)
    const [orders, setOrders] = useState({});
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);
    const [emailVisible, setEmailVisible] = useState(false);
    const [userIds, setUserIds] = useState({ userId: "", to: "" })
    const op = useRef(null);
    const cancelOp = useRef(null);

    const copyToClipboard = (text, message = 'Order ID') => {
        navigator.clipboard.writeText(text).then(() => {
            toast.current?.show({ severity: 'success', summary: 'Success', detail: `${message} has been copied to clipboard.` });
        }).catch(err => {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Failed to copy.' });
        });
    };

    const hasDeviceInfo = orders?.deviceInfo && Object.keys(orders.deviceInfo).length > 0;
    const handleMouseEnter = (event) => {
        op.current.show(event);
    };
    const handleMouseLeave = () => {
        op.current.hide();
    };
    const handleCancelMouseEnter = (event) => {
        cancelOp.current.show(event);
    };
    const handleCancelMouseLeave = () => {
        cancelOp.current.hide();
    };

    const items = [
        {
            label: params['screen'] === 'orders' ? 'Orders' : 'Customers',
            command: () => {
                params['screen'] === 'orders'
                    ? navigate('/orders')
                    : navigate('/users');
            }
        },
        {
            label: 'Orders',
            command: () => {
                params['screen'] === 'orders'
                    ? navigate('/orders')
                    : navigate('/user-orders/' + orders.userId);
            },
            visible: params['screen'] === 'user-orders'
        },
        {
            label: params.orderid,
            disabled: true,
            visible: params['screen'] === 'orders',
        },
        {
            template: (
                <PR.Button
                    icon="pi pi-copy"
                    className="p-button-text p-button-sm ml-20 icon-button"
                    onClick={() => copyToClipboard(orders.orderId)}
                    tooltip="Copy Order ID"
                    tooltipOptions={{ position: 'top' }}
                />
            ),
            visible: params['screen'] === 'orders',
        }
    ];

    const home = { icon: 'pi pi-home', url: '/' }

    const orderDetails = useCallback(() => {
        setLoading(true);

        const detailsResponse = (response) => {
            if (response.result === 'SUCCESS') {
                if (response.data && response.data.orderId) {
                    let data = response.data || {};
                    data.isSubscription = data?.dataPlans[0]?.isSubscription;
                    setOrders(data);
                    setError(false);
                } else {
                    setError(true);
                    toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'No Order Details available' });
                }
            }
            else {
                setError(true);
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: (error.errorMsg) ? error.errorMsg : error.summary })
            }
            setLoading(false);
        }

        getOrderDetails(params.orderid, headers, dispatch, detailsResponse)
    }, [dispatch, headers, params.orderid]);

    useEffect(() => {
        orderDetails();
    }, [orderDetails, refresh])

    const allStatuses = [...orderStatuses, ...deliveryStatuses];
    const orderStatus = (status) => {
        const statusVal = allStatuses?.filter(val => val.value === status)[0]?.label;
        return (
            <span
                className={`status ${status?.toLowerCase()}`}
            >{statusVal ?? status ?? 'N/A'}</span>
        );
    };

    const priceTemplate = rowData => {
        return (
            <div className="price tooltip-wrapper">
                <PriceDisplay rowData={rowData} />
            </div>
        )
    };


    const geteSIMsDescription = (esim) => {
        if (!!orders?.dataPlans && orders?.dataPlans.length === 0) {
            return '-'
        }
        if (!!esim && orders?.dataPlans[0]?.isSubscription) {
            return esim?.description?.split(" ")[0] + "_" + esim?.dataAmountForDisplay
        }
        const countryName = esim?.countries?.length ? esim?.countries[0]?.country?.name?.split("(")[0] : '';
        const description = countryName && esim.dataAmountForDisplay ? `${countryName} - ${esim.dataAmountForDisplay}` : esim.description;
        return description;
    }

    const handleRetryOrderAPI = () => {
        const saveNotesResponse = (response) => {
            if (response.result === "SUCCESS") {
                orderDetails();
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Recheck Status was successfully processed.`,
                });
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current?.show({
                    severity: error.severity,
                    summary: "Error",
                    detail: error.errorMsg ? error.errorMsg : error.summary,
                });
            }
        };
        retryOrderStatus(dispatch, headers, orders?.orderId, saveNotesResponse);
    };

    const discountType = (rowData) => {
        return rowData.promoCode && <div>
            <span>{formatPromoCode(rowData?.promoCode, (rowData.paymentGateway === 'prepaid'))}</span>
            <i
                title={rowData.paymentGateway === 'prepaid' ? 'Copy Voucher Code' : 'Copy Promocode'}
                className="pi pi-copy"
                style={{ color: 'red', marginLeft: '8px', cursor: 'pointer' }}
                onClick={() => copyToClipboard(formatPromoCode(rowData?.promoCode, (rowData.paymentGateway === 'prepaid')), (rowData.paymentGateway === 'prepaid' ? 'Voucher Code' : 'Promocode'))}
            />
        </div>
    }

    const transactionStatus = (rowData) => {
        return (<span
            className={`status-order ${rowData?.transactionStatus?.toLowerCase()}`}
        >{rowData?.transactionStatus ?? 'N/A'}</span>)
    }

    const paymentId = (rowData) => (
        <p className="payment-id-column">{rowData?.paymentMethodId}</p>
    );

    const transactionId = (rowData) => (
        <p className="payment-id-column">{rowData?.transactionId}</p>
    );

    const handleEsimNavigation = (iccid) => {
        if (!iccid) {
            return
        }
        const newTabUrl = `/esim-details/${iccid}`;
        window.open(newTabUrl, '_blank');
    }

    return (
        <>
            <div className="main">
                <PR.Toast ref={toast} position="top-right" />
                <div className="layout-sidebar">
                    <AdminHeader />
                </div>
                <div className="layout-content-wrapper">
                    <section className="admin-users-section order-details-section">
                        <div className="grid grid-nogutter">
                            <div className="col-12">
                                <div className="heading-sec flex align-items-center mb-2">
                                    <h1>Orders Details</h1>
                                    <div className="breadcrumb-section mobile-breadcrumb">
                                        <PR.BreadCrumb model={items} home={home} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {!loading ?
                            !error ?
                                <>
                                    <div className="users-data-table card">
                                        <div className="grid order-details-sec">
                                            <div className="col-12 md:col-12 list-box">
                                                <div className="flex align-items-start justify-content-between mobile-flex gap-3">
                                                    <div className="left ">
                                                        <div className="flex align-items-center justify-content-start iccid-msisdn-flex" >
                                                            <div className="center-align-div">
                                                                <b>ICCID: </b>
                                                                <b id="iccidTooltip" onClick={() => handleEsimNavigation(orders?.iccid)} className={orders?.iccid ? "iccid-tag" : "custom-target-icon"}> {orders.iccid ? orders.iccid : '-'} {orders.tag ? '(' + orders.tag + ')' : ''}</b>
                                                                {orders.iccid && <PR.Button
                                                                    icon="pi pi-copy"
                                                                    className="p-button-text p-button-sm ml-20 icon-button"
                                                                    onClick={() => copyToClipboard(orders.iccid, "ICCID")}
                                                                    tooltip="Copy ICCID"
                                                                    tooltipOptions={{ position: 'top' }}
                                                                />}
                                                                <PR.Tooltip
                                                                    target="#iccidTooltip"
                                                                    content={orders?.iccid ? `View eSIM & bundle details` : "No ICCID available"}
                                                                    position="bottom"
                                                                />
                                                            </div>
                                                            {orders?.msisdn && <div className="center-align-div border-left">
                                                                <b>MSISDN: </b>
                                                                <b> {utils.formatUKPhoneNumber(orders.msisdn)}</b>
                                                                <PR.Button
                                                                    icon="pi pi-copy"
                                                                    className="p-button-text p-button-sm ml-20 icon-button"
                                                                    onClick={() => copyToClipboard(orders.msisdn, "MSISDN")}
                                                                    tooltip="Copy MSISDN"
                                                                    tooltipOptions={{ position: 'top' }}
                                                                />
                                                            </div>}
                                                        </div>
                                                        <p className="text-gray-600 mt-1 mb-0 text-sm">Short Order ID: {(orders.orderId.substring(0, 8))}</p>
                                                    </div>
                                                    <div className="right flex align-items-center gap-3">
                                                        <div className="flex gap-1 align-items-center">
                                                            <p>Order Status: {orderStatus(orders.orderStatus)}</p>
                                                            <PR.Button
                                                                disabled={orders.orderStatus !== "PAYMENT_FAILED" && orders.orderStatus !== "PURCHASE_FAILED"}
                                                                icon="pi pi-refresh"
                                                                className="p-button-sm p-button-secondary refresh-btn"
                                                                tooltip="Recheck Status"
                                                                tooltipOptions={{ position: 'bottom' }}
                                                                onClick={() => handleRetryOrderAPI()}
                                                            />
                                                        </div>
                                                        <div className="flex gap-3 justify-content-between">
                                                            {orders?.orderType === 'SUBSCRIPTION' ? (
                                                                <p>
                                                                    Subscription: {orders.subscriptionStatus ? (
                                                                        <span
                                                                            className={"statusFields " + orders.subscriptionStatus + (orders.subscriptionStatus === "CANCELLING" ? " cursor-pointer" : "")}
                                                                            style={orders.subscriptionStatus === "CANCELLING" ? { display: 'inline-flex', alignItems: 'center' } : {}}
                                                                            onMouseEnter={orders.subscriptionStatus === "CANCELLING" ? handleCancelMouseEnter : null}
                                                                            onMouseLeave={orders.subscriptionStatus === "CANCELLING" ? handleCancelMouseLeave : null}
                                                                        >
                                                                            {orders.subscriptionStatus}
                                                                            {orders.subscriptionStatus === "CANCELLING" && (
                                                                                <i className="pi pi-info-circle ml-1" />
                                                                            )}
                                                                        </span>
                                                                    ) : '-'}
                                                                </p>
                                                            ) : (
                                                                <p>eSIM Status: {orders.esimStatus ? <span className={"statusFields " + orders.esimStatus}>{orders.esimStatus}</span> : '-'}</p>
                                                            )}
                                                            {orders?.dataPlans?.some(plan => plan?.simtype === "pSIM") && (
                                                                <div className="flex sim-indicator physical-sim">
                                                                    <i className="pi pi-mobile" />
                                                                    Physical SIM
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-1 justify-content-between1">
                                                            <PR.Button
                                                                onClick={() => { setEmailVisible(true); setUserIds({ userId: orders?.userId, to: orders?.email }) }}
                                                                className="p-button-text icon-button email-icon"
                                                                tooltip="Send Email"
                                                                tooltipOptions={{ position: 'bottom' }}>
                                                                <PR.Image src={emailRed} alt="Send Email" />
                                                            </PR.Button>

                                                            <>
                                                                <PR.Button
                                                                    icon="pi pi-info-circle"
                                                                    className="icon-button"
                                                                    onMouseEnter={handleMouseEnter}
                                                                    onMouseLeave={handleMouseLeave}
                                                                />

                                                                <PR.OverlayPanel ref={op} className="info-overlay-panel" dismissable style={{ backgroundColor: '#3a4668', padding: '2px', borderRadius: '8px', width: '300px' }}>
                                                                    <div
                                                                        style={{
                                                                            marginBottom: '0.75rem',
                                                                            color: '#fff',
                                                                            fontSize: '14px'
                                                                        }}
                                                                    >
                                                                        {orders?.isEsimCompatible
                                                                            ? 'Purchase was made on a device that supports eSIM'
                                                                            : 'Purchase was made on a device that does not support eSIM'}
                                                                    </div>
                                                                    {hasDeviceInfo ? (
                                                                        <>
                                                                            <div style={{ marginBottom: '0.3rem', textDecoration: 'underline', color: '#fff', fontSize: '14px' }}>Device Information</div>
                                                                            <ul style={{ margin: 0, paddingLeft: '0', listStyleType: 'none', color: '#fff', fontSize: '14px' }}>
                                                                                {Object.entries(orders.deviceInfo)
                                                                                    .filter(
                                                                                        ([key, value]) =>
                                                                                            key !== 'deviceToken' && value && value !== '--'
                                                                                    )
                                                                                    .map(([key, value]) => (
                                                                                        <li key={key} style={{ marginBottom: '0.25rem' }}>
                                                                                            <strong>{key}</strong>: {value}
                                                                                        </li>
                                                                                    ))}
                                                                            </ul>
                                                                        </>
                                                                    ) : (
                                                                        <div style={{
                                                                            color: '#fff',
                                                                            fontSize: '14px'
                                                                        }}>- No device information available -</div>
                                                                    )}
                                                                </PR.OverlayPanel>
                                                            </>
                                                            {orders?.subscriptionStatus === "CANCELLING" && (
                                                                <PR.OverlayPanel ref={cancelOp} dismissable style={{ backgroundColor: '#3a4668', padding: '4px', borderRadius: '8px', width: '300px' }}>
                                                                    <div style={{ color: '#fff', fontSize: '14px', maxHeight: '200px', overflowY: 'auto', padding: '10px' }}>
                                                                        <div style={{ marginBottom: '5px', fontWeight: 'bold', textDecoration: 'underline' }}>Cancellation Reason</div>
                                                                        {orders?.cancellationReason || '- No reason provided -'}
                                                                    </div>
                                                                </PR.OverlayPanel>
                                                            )}
                                                            <OrderActionMenu orders={orders} refreshData={() => { orderDetails(); setRefresh(!refresh); }} toast={toast} showRecheckEsimStatus={true} displaySendEmailHandler={() => { setEmailVisible(true); setUserIds({ userId: orders?.userId, to: orders?.email }) }} />
                                                        </div>
                                                    </div>
                                                </div>
                                                {orders?.portingInfo && (
                                                    <div className="porting-info-container mt-2">
                                                        <div className="porting-info-badge">
                                                            <b className="heading"><i class="pi pi-arrow-right-arrow-left mr-1"></i> Porting Info:</b>
                                                            {orders.portingInfo.pac && (
                                                                <>
                                                                    <span className="porting-detail">
                                                                        PAC: <b>{orders.portingInfo.pac}</b>
                                                                        <PR.Button
                                                                            icon="pi pi-copy"
                                                                            className="p-button-text p-button-sm icon-button"
                                                                            onClick={() => copyToClipboard(orders.portingInfo.pac, "PAC Code")}
                                                                            tooltip="Copy PAC Code"
                                                                            tooltipOptions={{ position: 'top' }}
                                                                        />
                                                                    </span>
                                                                    {orders.portingInfo.stac && <span className="porting-divider-dot">•</span>}
                                                                </>
                                                            )}
                                                            {orders.portingInfo.stac && (
                                                                <span className="porting-detail">
                                                                    STAC: <b>{orders.portingInfo.stac}</b>
                                                                    <PR.Button
                                                                        icon="pi pi-copy"
                                                                        className="p-button-text p-button-sm icon-button"
                                                                        onClick={() => copyToClipboard(orders.portingInfo.stac, "STAC Code")}
                                                                        tooltip="Copy STAC Code"
                                                                        tooltipOptions={{ position: 'top' }}
                                                                    />
                                                                </span>
                                                            )}
                                                            {!orders.portingInfo.pac && !orders.portingInfo.stac && (
                                                                <span className="porting-detail">
                                                                    PAC: <b>-</b>
                                                                </span>
                                                            )}
                                                            <span className="porting-divider-dot">•</span>
                                                            <span className="porting-detail">
                                                                Mobile: <b>{utils.formatUKPhoneNumber(orders.portingInfo.mobileNumber) || orders.portingInfo.mobileNumber || '-'}</b>
                                                                {orders.portingInfo.mobileNumber && (
                                                                    <PR.Button
                                                                        icon="pi pi-copy"
                                                                        className="p-button-text p-button-sm icon-button"
                                                                        onClick={() => copyToClipboard(orders.portingInfo.mobileNumber, "Porting Mobile Number")}
                                                                        tooltip="Copy Porting Mobile Number"
                                                                        tooltipOptions={{ position: 'top' }}
                                                                    />
                                                                )}
                                                            </span>
                                                            <span className="porting-divider-dot">•</span>
                                                            <span className="porting-detail">
                                                                Confirmed Date: <b>{formatDate(orders.portingInfo.confirmedDate) || '-'}</b>
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-12 md:col-12">
                                                <PR.DataTable
                                                    value={[{ ...orders }]}
                                                    rows={10} className="plan-detials-datatable">
                                                    <PR.Column body={() => (
                                                        <div className="flex align-items-center">
                                                            {orders.providerName}
                                                            {(() => {
                                                                const group = orders?.dataPlans?.[0]?.groups;
                                                                if (!group) return null;
                                                                const lowerGroup = group.toLowerCase();
                                                                let iconClass = "";
                                                                let tooltip = "";
                                                                if (lowerGroup === "domestic") {
                                                                    iconClass = "pi pi-home";
                                                                    tooltip = "Domestic";
                                                                } else if (lowerGroup === "iot") {
                                                                    return null;
                                                                } else {
                                                                    iconClass = "pi pi-airplane";
                                                                    tooltip = "Travel";
                                                                }
                                                                return <i className={`${iconClass} ml-2`} style={{ color: 'red', fontSize: '13px' }} title={tooltip}></i>;
                                                            })()}
                                                        </div>
                                                    )} header="Provider Name" style={{ width: "10%" }}></PR.Column>
                                                    <PR.Column body={geteSIMsDescription(orders?.dataPlans.length && orders.dataPlans[0])} header="Plan" style={{ width: "15%" }}></PR.Column>
                                                    {orders?.orderType === 'SUBSCRIPTION' ? null : (
                                                        <PR.Column body={() => (
                                                            orders?.dataPlans.length ? (
                                                                <div>
                                                                    <div>{orders.dataPlans[0].name}</div>
                                                                    <div style={{ marginTop: "4px" }}>
                                                                        {orders.dataPlans[0].groups}
                                                                    </div>
                                                                </div>
                                                            ) : "-"
                                                        )}
                                                            header="Bundle Name" style={{ width: "10%" }}>
                                                        </PR.Column>
                                                    )}
                                                    <PR.Column
                                                        header="Order Type"
                                                        body={orders?.dataPlans.length ? (
                                                            (orders?.subscriptionStatus) ? (
                                                                <span className="label recurring" style={{ backgroundColor: orders?.subscriptionStatus === 'ACTIVE' ? '#38a169' : '#e53e3e', color: '#fff', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                                    <i className="pi pi-sync" style={{ marginRight: '4px', fontSize: '12px' }}></i>Recurring
                                                                </span>
                                                            ) : orders?.istopup === "true" ? (
                                                                <span className="label topup" style={{ backgroundColor: '#0284c7', color: '#fff', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                                    <i className="pi pi-arrow-up" style={{ marginRight: '4px', fontSize: '12px' }}></i>Topup
                                                                </span>
                                                            ) : (
                                                                <span className="label regular" style={{ backgroundColor: '#e2e8f0', color: '#4a5568', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                                                    <i className="pi pi-save" style={{ marginRight: '4px' }}></i>Regular
                                                                </span>
                                                            )
                                                        ) : "-"}
                                                        style={{ width: "10%" }}
                                                    />
                                                    <PR.Column
                                                        header="Purchased On"
                                                        body={formatDateTime(orders.createdTs)}
                                                        style={{ width: "10%" }}
                                                    />
                                                    {orders?.dataPlans[0]?.isSubscription ? <PR.Column
                                                        header="Expiry/Renewal date"
                                                        body={formatDateTime(orders?.nextRenewalDate)}
                                                        style={{ width: "10%" }}
                                                    /> : <PR.Column
                                                        header="Expiry On"
                                                        body={formatDateTime(orders?.bundleEndTime)}
                                                        style={{ width: "10%" }}
                                                    />}
                                                    <PR.Column header="Price" body={priceTemplate} style={{ width: "10%" }}></PR.Column>
                                                </PR.DataTable>
                                            </div>
                                        </div>


                                        <div className="grid order-details-sec">
                                            <div className="col-12 md:col-6">
                                                <div className="list-box">
                                                    <PR.Panel header="Customer Details">
                                                        <ul className="user-data-ul">
                                                            {(orders.contactInfo?.firstName || orders.contactInfo?.lastName) && <li><span>Name : </span> <span className="user-data-span">{orders.contactInfo?.firstName} {orders.contactInfo?.lastName}</span></li>}
                                                            {orders.contactInfo?.email && <li><span>Email : </span> <span className="user-data-span">{orders.contactInfo?.email} </span></li>}
                                                            <li><span>Phone Number : </span> <span className="user-data-span">{!!orders.contactInfo?.phone?.localPhoneNumber ? orders.contactInfo?.phone?.callingCode + " " + orders.contactInfo?.phone?.localPhoneNumber : '-'}</span>                                                            </li>
                                                            <li><span>IP Address : </span> {orders.ipAddress ? (<span className="user-data-span">{orders?.ipAddress} </span>) : (<span className="user-data-span"> - </span>)}</li>

                                                            {orders.paymentGateway === 'Stripe' && <>
                                                                {orders.billingAddress?.address && <li><span>House No/Building : </span> <span className="user-data-span">{orders.billingAddress?.address} </span> </li>}
                                                                {orders.billingAddress?.street && <li><span>Street : </span> <span className="user-data-span">{orders.billingAddress?.street}</span> </li>}
                                                                {orders.billingAddress?.city && <li><span>City : </span><span className="user-data-span">{orders.billingAddress?.city}</span> </li>}
                                                                {orders.billingAddress?.city && <li><span>Postcode : </span><span className="user-data-span"> {orders.billingAddress?.postCode}</span> </li>}
                                                                {orders.billingAddress?.country && <li><span>Country : </span> <span className="user-data-span">{orders.billingAddress?.country}</span> </li>}
                                                            </>}
                                                        </ul>
                                                    </PR.Panel>
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-6">
                                                <div className="list-box">
                                                    <Notes orderId={orders.orderId} refresh={refresh} />
                                                </div>
                                            </div>
                                        </div>
                                        {!!orders?.transactions ? <div className="grid order-details-sec">
                                            <p className="ml-2 transaction-heading-p">Transaction history</p>
                                            <div className="col-12 md:col-12 -mt-3">
                                                {orders?.dataPlans[0]?.isSubscription ? <PR.DataTable className="transaction-history-datatable"
                                                    value={orders?.transactions} rows={10} >
                                                    <PR.Column className="no-highlight" field="transactionId" sortable body={(rowData) => rowData?.transactionId} header="Transaction ID" style={{ width: "16%" }} />
                                                    <PR.Column className="no-highlight" field="paymentMethodId" sortable body={(rowData) => rowData?.paymentMethodId} header="Payment Method ID" style={{ width: "18%" }} />
                                                    <PR.Column className="no-highlight" field="totalOrderAmount" sortable header="Value" body={(rowData) => utils.formatPriceCurrency(rowData?.priceCurrency, rowData?.totalOrderAmount)} style={{ width: "8%" }} />
                                                    <PR.Column className="no-highlight" field="paymentGateway" sortable header="Payment Gateway" body={(rowData) => rowData?.paymentGateway} style={{ width: "10%" }} />
                                                    <PR.Column className="no-highlight" field="createdTs" sortable header="Date" body={(rowData) => formatDateTime(rowData?.createdTs)} style={{ width: "8%" }} />
                                                    <PR.Column className="no-highlight" field="transactionStatus" sortable header="Transaction status" body={transactionStatus} style={{ width: "10%" }} />
                                                </PR.DataTable>
                                                    : <PR.DataTable value={[...orders?.transactions]} rows={10} className="transaction-history-datatable">
                                                        <PR.Column className="no-highlight" field="transactionId" sortable body={transactionId} header="Transaction ID" style={{ width: "16%" }} />
                                                        <PR.Column className="no-highlight" field="paymentMethodId" body={paymentId} sortable header="Payment Method ID" style={{ width: "18%" }} />
                                                        <PR.Column className="no-highlight" field="totalOrderAmount" sortable header="Value" body={(rowData) => utils.formatPriceCurrency(rowData?.priceCurrency, rowData?.totalOrderAmount)} style={{ width: "8%" }} />
                                                        <PR.Column className="no-highlight" field="paymentGateway" sortable header="Payment Gateway" body={(rowData) => rowData?.paymentGateway ? ((rowData.paymentGateway === "Stripe") ? "Debit / Credit Card" : utils.firstLetterCapital(rowData.paymentGateway)) : '-'} style={{ width: "10%" }} />
                                                        {orders.promoCode ? <PR.Column className="no-highlight promo-voucher" field="promoCode" sortable header={orders.paymentGateway === 'prepaid' ? 'Voucher Code' : 'Promocode'} style={{ width: "10%" }} body={(rowData) => discountType(orders)} /> : null}
                                                        <PR.Column className="no-highlight" field="createdTs" sortable header="Date" body={(rowData) => formatDateTime(rowData?.createdTs)} style={{ width: "8%" }} />
                                                        <PR.Column className="no-highlight tstatus" field="transactionStatus" sortable header="Transaction status" body={transactionStatus} style={{ width: "10%" }} />
                                                    </PR.DataTable>}

                                            </div>
                                        </div> : null}

                                    </div>
                                </> :
                                <section className="admin-users-section">
                                    <div className="col-12 text-center">
                                        <h6 className="font-semibold text-lg"> No details found</h6>
                                    </div>
                                </section> : ''
                        }
                        {loading && (
                            <div className="col-12 text-center">
                                <div className="progress-spinner">
                                    <PR.ProgressSpinner
                                        className="progressSpinner"
                                        animationDuration=".5s"
                                    />
                                </div>
                            </div>
                        )}
                        <SendEmail
                            visible={emailVisible}
                            userId={userIds}
                            onClose={() => setEmailVisible(false)}
                        />
                    </section>
                    <AdminFooter />
                </div>
            </div>
        </>
    );
};

export default OrderDetails;
